"use client";

import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import type { AppDispatch, RootState } from "@/store";
import { fetchBillingTokenStatus } from "@/store/modules/establishmentBilling/thunks";
import { useAuth } from "@/context/AuthContext";
import type { BillingTokenStatus } from "@/types/billing";

const readinessKeywords = [
  "completed",
  "complete",
  "approved",
  "active",
  "ready",
];

export const isBillingTokenReady = (
  tokenStatus?: BillingTokenStatus | null,
): boolean => {
  if (!tokenStatus) return false;

  const booleanCandidates = [
    tokenStatus.has_token,
    tokenStatus.hasToken,
    tokenStatus.has_bank_account,
    tokenStatus.hasBankAccount,
    tokenStatus.bank_account_registered,
    tokenStatus.is_complete,
  ];

  for (const candidate of booleanCandidates) {
    if (typeof candidate === "boolean") {
      return candidate;
    }
  }

  if (tokenStatus.token && typeof tokenStatus.token === "object") {
    return true;
  }

  if (typeof tokenStatus.status === "string") {
    const normalized = tokenStatus.status.toLowerCase();
    return readinessKeywords.some((keyword) => normalized.includes(keyword));
  }

  return false;
};

export interface UseBillingTokenStatusReturn {
  tokenStatus: RootState["establishmentBilling"]["tokenStatus"];
  loadingTokenStatus: boolean;
  hasBankAccount: boolean;
  needsBankAccount: boolean;
  creatingToken: boolean;
  refreshTokenStatus: (force?: boolean) => Promise<void>;
}

export const useBillingTokenStatus = (): UseBillingTokenStatusReturn => {
  const dispatch: AppDispatch = useDispatch();
  const { baseURL, isLoading } = useAuth();

  const { tokenStatus, loadingTokenStatus, creatingToken } = useSelector(
    (state: RootState) => state.establishmentBilling,
  );

  const refreshTokenStatus = useCallback(
    async (force = false) => {
      if (!baseURL || isLoading) return;
      if (!force) {
        if (loadingTokenStatus) return;
        if (tokenStatus) return;
      }
      try {
        await dispatch(fetchBillingTokenStatus()).unwrap();
      } catch (error) {
        // handled in slice toast/state
        console.error("Error refreshing billing token status:", error);
      }
    },
    [dispatch, baseURL, isLoading, loadingTokenStatus, tokenStatus],
  );

  useEffect(() => {
    if (!baseURL || isLoading) return;
    if (tokenStatus) return;
    void refreshTokenStatus();
  }, [baseURL, isLoading, tokenStatus, refreshTokenStatus]);
  const hasBankAccount = useMemo(
    () => isBillingTokenReady(tokenStatus),
    [tokenStatus],
  );

  return {
    tokenStatus,
    loadingTokenStatus,
    hasBankAccount,
    creatingToken,
    needsBankAccount: !hasBankAccount,
    refreshTokenStatus,
  };
};
